import { authorTitleShortLabel } from '../../../utils/authorTitle';
import { getBookLanguageFlagFilename, LANGUAGE_ENTRIES } from '../../../utils/globals';
import { bookLanguageFlagPublicUrl } from './bookLanguageFlags';

export interface CategoryRow {
  id: string;
  /** Labels by book language code (`BookLanguageRow.id`). Legacy rows may still be a single string until migrated. */
  name?: Record<string, string> | string;
  /** Persisted on API (`CategoryModel`); preferred when `name` is absent. */
  name_map?: Record<string, string> | string;
  /** S3 key under the libro data bucket (`category/…`). */
  image_path?: string | null;
  updated_at: string;
  /** Present on API (`CategoryModel`); optional for older clients. */
  updated_by?: string;
  created_at?: string;
  created_by?: string;
}

export interface AuthorRow {
  id: string;
  /** Localized names (API `name_map`). Legacy rows may use a single string. */
  name_map: Record<string, string> | string;
  /** Title code (`dr`, `prof`, …); omit on legacy rows. */
  title?: string;
  updated_at: string;
  updated_by: string;
  created_at: string;
  created_by: string;
}

export interface BookLanguageRow {
  id: string;
  code: string;
  name: string;
  /** Basename under `public/flags/` (e.g. `mongolia.png`). */
  filename?: string;
  /** @deprecated Legacy Dynamo attribute; prefer `filename`. */
  flag_url?: string;
  /** When false, omitted from public app bundles; omitted/undefined rows are treated as active. */
  active?: boolean;
  updated_at: string;
}

/** Matches API: legacy rows without `active` are active. */
export function bookLanguageRowIsActive(row: Pick<BookLanguageRow, 'active'>): boolean {
  return row.active !== false;
}

function localizedNameFlatFromRow(
  n: Record<string, string> | string | undefined,
): Record<string, string> {
  if (n === undefined) return {};
  if (typeof n === 'string') {
    const t = n.trim();
    if (!t) return {};
    const first = LANGUAGE_ENTRIES[0];
    return first ? { [first.code]: t } : {};
  }
  if (n && typeof n === 'object' && !Array.isArray(n)) {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(n as Record<string, unknown>)) {
      const s = typeof v === 'string' ? v.trim() : v != null ? String(v).trim() : '';
      if (s) out[k] = s;
    }
    return out;
  }
  return {};
}

function categoryNameFlatFromRow(row: CategoryRow): Record<string, string> {
  return localizedNameFlatFromRow(row.name ?? row.name_map);
}

/** All localized entries from a category row (for editors keyed by book language id). */
export function categoryNameMapFromRow(row: CategoryRow): Record<string, string> {
  return { ...categoryNameFlatFromRow(row) };
}

function authorNameFlatFromRow(row: AuthorRow): Record<string, string> {
  return localizedNameFlatFromRow(row.name_map);
}

/** All localized entries from an author row (for editors keyed by book language id). */
export function authorNameMapFromRow(row: AuthorRow): Record<string, string> {
  return { ...authorNameFlatFromRow(row) };
}

/** Table / delete label: first non-empty label in `LANGUAGE_ENTRIES` order, else id. */
export function categoryRowSummaryLabel(row: CategoryRow): string {
  const flat = categoryNameFlatFromRow(row);
  for (const e of LANGUAGE_ENTRIES) {
    const t = flat[e.code]?.trim();
    if (t) return t;
  }
  return row.id;
}

export function authorRowSummaryLabel(row: AuthorRow): string {
  const flat = authorNameFlatFromRow(row);
  for (const e of LANGUAGE_ENTRIES) {
    const t = flat[e.code]?.trim();
    if (t) return t;
  }
  return row.id;
}

/** Summary for confirmations (title + first name). */
export function authorRowSummaryWithTitle(row: AuthorRow): string {
  const p = authorTitleShortLabel(row.title);
  const n = authorRowSummaryLabel(row);
  return p ? `${p} ${n}` : n;
}

export interface AgeGroupRow {
  id: string;
  start?: number;
  end?: number;
  /** @deprecated Legacy rows; prefer start/end. */
  name?: string;
  updated_at: string;
}

/** Table / display: interval or legacy name. */
export function ageGroupIntervalLabel(row: AgeGroupRow): string {
  const s = row.start;
  const e = row.end;
  if (typeof s === 'number' && typeof e === 'number' && !Number.isNaN(s) && !Number.isNaN(e)) {
    return `${s} – ${e}`;
  }
  const n = row.name?.trim();
  return n && n.length > 0 ? n : '—';
}

/** Filename for forms, including best-effort parse from legacy `flag_url`. */
export function bookLanguageDraftFilename(row: BookLanguageRow): string {
  const f = row.filename?.trim();
  if (f) return f;
  const u = row.flag_url?.trim();
  if (!u) return '';
  const tail = u.includes('/') ? (u.split('/').pop() ?? '') : u;
  return tail.split('?')[0] ?? '';
}

/** Image URL for table: `filename` or legacy same-app `/flags/…` paths only. */
export function bookLanguageFlagTableSrc(row: BookLanguageRow): string | null {
  const fn = row.filename?.trim();
  const base = process.env.PUBLIC_URL ?? '';
  if (fn) return `${base}/flags/${fn}`;
  const u = row.flag_url?.trim();
  if (u?.startsWith('/flags/')) return `${base}${u}`;
  return null;
}

/** Flag URL for forms and inline UI: row storage first, then default from `LANGUAGE_ENTRIES` by id/code. */
export function bookLanguageFlagDisplaySrc(row: BookLanguageRow): string | null {
  const fromRow = bookLanguageFlagTableSrc(row);
  if (fromRow) return fromRow;
  const fn =
    getBookLanguageFlagFilename(row.id) ?? getBookLanguageFlagFilename(row.code);
  return fn ? bookLanguageFlagPublicUrl(fn) : null;
}

export interface SettingListRow {
  id: string;
  value: string;
  value_en: string;
  link?: string;
  fileUrl?: string;
  updated_at: string;
}

export interface ListResponse {
  data: unknown[];
}

export function formatSettingsRowDate(iso: string | undefined): string {
  if (iso == null || String(iso).trim() === '') return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('mn-MN', { dateStyle: 'short', timeStyle: 'short' });
}

/** Shown when creating a book_language whose id/code already exists (matches API 409). */
export const BOOK_LANGUAGE_DUPLICATE_CODE_MESSAGE =
  'Энэ кодтой хэл аль хэдийн байна. Жагсаалтаас засварлана уу.';

export function settingsListApiError(e: unknown, fallback: string): string {
  if (typeof e === 'object' && e !== null && 'response' in e) {
    const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
    if (msg != null && String(msg).trim() !== '') return String(msg);
  }
  return fallback;
}
